import React from 'react';
import { Widgets } from 'react-awesome-query-builder';
import AntdConfig from 'react-awesome-query-builder/lib/config/antd';
import en_US from 'antd/es/locale-provider/en_US';
import moment from 'moment';
import merge from 'lodash/merge';

const { ValueFieldWidget, DateWidget } = Widgets;

const customConfig = {
  conjunctions: {
    AND: {
      label: 'And',
      mongoConj: '$and',
      reversedConj: 'OR',
      formatConj: (children, conj, not, isForDisplay) => {
        return children.size > 1
          ? (not ? 'NOT ' : '') +
              '(' +
              children.join(' ' + (isForDisplay ? 'AND' : '&&') + ' ') +
              ')'
          : (not ? 'NOT (' : '') + children.first() + (not ? ')' : '');
      },
    },
    OR: {
      label: 'Or',
      mongoConj: '$or',
      reversedConj: 'AND',
      formatConj: (children, conj, not, isForDisplay) => {
        return children.size > 1
          ? (not ? 'NOT ' : '') +
              '(' +
              children.join(' ' + (isForDisplay ? 'OR' : '||') + ' ') +
              ')'
          : (not ? 'NOT (' : '') + children.first() + (not ? ')' : '');
      },
    },
  },
  fields: {
    credit_score: {
      label: 'Credit Score',
      type: 'number',
      fieldSettings: {
        min: 0,
      },
    },
    rent_to_income: {
      label: 'Rent to Income',
      type: 'number',
      fieldSettings: {
        min: 0,
        max: 1,
      },
    },
    previous_resident: {
      label: 'Was Previous Resident',
      type: 'boolean',
    },
    birthday: {
      label: 'Birthday',
      type: 'number',
      fieldSettings: {
        min: 0,
        max: 120,
      },
    },
  },
  types: {
    boolean: {
      widgets: {
        boolean: {
          operators: ['equal'],
          widgetProps: { hideOperator: true },
        },
        field: {
          operators: ['equal'],
        },
      },
    },
    date: {
      widgets: {
        date: {
          operators: ['equal', 'less', 'less_or_equal', 'greater', 'greater_or_equal', 'between'],
        },
      },
    },
  },
  operators: {
    equal: {
      label: 'equals',
      labelForFormat: '=',
      reversedOp: 'not_equal',
    },
    not_equal: {
      label: 'not equal',
      labelForFormat: '!=',
      reversedOp: 'equal',
      mongoFormatOp: (field, op, value) => ({ [field]: { $ne: value } }),
    },
    less: {
      label: 'less than',
      labelForFormat: '<',
      reversedOp: 'greater_or_equal',
      mongoFormatOp: (field, op, value) => ({ [field]: { $lt: value } }),
    },
    less_or_equal: {
      label: 'less than or equal to',
      labelForFormat: '<=',
      reversedOp: 'greater',
      mongoFormatOp: (field, op, value) => ({ [field]: { $lte: value } }),
    },
    greater: {
      label: 'greater than',
      labelForFormat: '>',
      reversedOp: 'less_or_equal',
      mongoFormatOp: (field, op, value) => ({ [field]: { $gt: value } }),
    },
    greater_or_equal: {
      label: 'greater than or equal to',
      labelForFormat: '>=',
      reversedOp: 'less',
      mongoFormatOp: (field, op, value) => ({ [field]: { $gte: value } }),
    },
    between: {
      label: 'Between',
      labelForFormat: 'BETWEEN',
      cardinality: 2,
      formatOp: (
        field,
        op,
        values,
        valueSrcs,
        valueTypes,
        opDef,
        operatorOptions,
        isForDisplay
      ) => {
        let valFrom = values.first();
        let valTo = values.get(1);
        if (isForDisplay) return `${field} >= ${valFrom} AND ${field} <= ${valTo}`;
        else return `${field} >= ${valFrom} && ${field} <= ${valTo}`;
      },
      mongoFormatOp: (field, op, values) => ({ [field]: { $gte: values[0], $lte: values[1] } }),
      valueLabels: ['Value from', 'Value to'],
      textSeparators: [null, 'and'],
    },
  },
  widgets: {
    field: {
      valueSrc: 'field',
      factory: (props) => <ValueFieldWidget {...props} />,
      formatValue: (val, fieldDef, wgtDef, isForDisplay, valFieldDef) => {
        return isForDisplay ? valFieldDef.label || val : val;
      },
      valueLabel: 'Field to compare',
      valuePlaceholder: 'Select field to compare',
      customProps: {
        showSearch: true,
      },
    },
    date: {
      type: 'date',
      valueSrc: 'value',
      factory: (props) => <DateWidget {...props} />,
      label: 'Select Date',
      dateFormat: 'DD.MM.YYYY',
      valueFormat: 'YYYY-MM-DD',
      formatValue: (val, fieldDef, wgtDef, isForDisplay) => {
        let dateVal = moment(val, wgtDef.valueFormat);
        return isForDisplay ? '"' + dateVal.format(wgtDef.dateFormat) + '"' : JSON.stringify(val);
      },
    },
  },
  settings: {
    ...AntdConfig.settings,
    locale: {
      short: 'en',
      full: 'en-US',
      antd: en_US,
    },
    maxLabelsLength: 50,
    hideConjForOne: true,
    renderSize: 'small',
    renderConjsAsRadios: false,
    renderFieldAndOpAsDropdown: false,
    customFieldSelectProps: {
      showSearch: true,
    },
    groupActionsPosition: 'topRight', // oneOf [topLeft, topCenter, topRight, bottomLeft, bottomCenter, bottomRight]
    setOpOnChangeField: ['keep', 'default'], // 'default' (default if present), 'keep' (keep prev from last field), 'first', 'none'
    clearValueOnChangeField: false, //false - if prev & next fields have same type (widget), keep
    clearValueOnChangeOp: false,
    setDefaultFieldAndOp: false,
    maxNesting: 1,
    fieldSeparator: '.',
    fieldSeparatorDisplay: '->',
    showLabels: false,
    valueLabel: 'Value',
    valuePlaceholder: 'Value',
    fieldLabel: 'Field',
    operatorLabel: 'Operator',
    fieldPlaceholder: 'Select field',
    operatorPlaceholder: 'Select operator',
    deleteLabel: null,
    addGroupLabel: 'Add group',
    addRuleLabel: 'Add rule',
    immutableGroupsMode: false,
    notLabel: 'Not',
    showNot: false,
    delGroupLabel: null,
    canLeaveEmptyGroup: true, //after deletion
    formatReverse: (q, isForDisplay) => {
      if (isForDisplay) return 'NOT(' + q + ')';
      else return '!(' + q + ')';
    },
    formatField: (field, parts, label2, fieldDefinition, config, isForDisplay) => {
      if (isForDisplay) return label2;
      else return field;
    },
    valueSourcesInfo: {
      value: {
        label: 'Value',
      },
      field: {
        label: 'Field',
        widget: 'field',
      },
    },
    valueSourcesPopupTitle: 'Select value source',
    canReorder: true,
  },
};

const config = merge(AntdConfig, customConfig);

export default config;
